import { useState, useEffect } from 'react';
import { useTheme } from '../../hooks/useTheme';
import { DEFAULT_DARK_THEME, DEFAULT_LIGHT_THEME } from '../../constants/themeDefaults';
import { cloneTheme } from '../../utils/themeUtils';
import ColorPicker from './ColorPicker';
import ThemePreview from './ThemePreview';
import toast from 'react-hot-toast';

/**
 * Theme Editor Modal
 *
 * Full-featured theme customization interface with:
 * - Tabbed sections for different color categories
 * - Live preview
 * - Preset reset buttons
 * - Save/Cancel actions
 */
export default function ThemeEditor({ isOpen, onClose }) {
  const { customTheme, updateCustomTheme, resetCustomTheme, canEditTheme } = useTheme();
  const [editingTheme, setEditingTheme] = useState(null);
  const [activeTab, setActiveTab] = useState('background');
  const [hasChanges, setHasChanges] = useState(false);

  // Initialize editing theme when modal opens
  useEffect(() => {
    if (isOpen && customTheme) {
      setEditingTheme(cloneTheme(customTheme));
      setHasChanges(false);
    }
  }, [isOpen, customTheme]);

  if (!isOpen || !editingTheme) return null;

  const updateColor = (path, value) => {
    const pathParts = path.split('.');
    const updated = cloneTheme(editingTheme);

    // Navigate to the correct nested property
    let current = updated.colors;
    for (let i = 0; i < pathParts.length - 1; i++) {
      current = current[pathParts[i]];
    }
    current[pathParts[pathParts.length - 1]] = value;

    setEditingTheme(updated);
    setHasChanges(true);
  };

  const handleSave = async () => {
    const success = await updateCustomTheme(editingTheme);
    if (success) {
      setHasChanges(false);
      onClose();
    }
  };

  const handleCancel = () => {
    if (hasChanges) {
      if (window.confirm('You have unsaved changes. Are you sure you want to close?')) {
        onClose();
      }
    } else {
      onClose();
    }
  };

  const handleReset = (preset) => {
    const confirmed = window.confirm(`Reset to ${preset} theme? This will replace all your custom colors.`);
    if (confirmed) {
      const baseTheme = preset === 'dark' ? DEFAULT_DARK_THEME : DEFAULT_LIGHT_THEME;
      setEditingTheme({
        ...editingTheme,
        colors: cloneTheme(baseTheme).colors
      });
      setHasChanges(true);
      toast.success(`Reset to ${preset} theme`);
    }
  };

  const tabs = [
    { id: 'background', label: 'Background', icon: '🎨' },
    { id: 'text', label: 'Text', icon: '📝' },
    { id: 'border', label: 'Borders', icon: '🔲' },
    { id: 'accent', label: 'Accent', icon: '⭐' },
    { id: 'semantic', label: 'Semantic', icon: '🚦' },
    { id: 'other', label: 'Other', icon: '⚙️' }
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onClick={handleCancel}
      />

      {/* Modal */}
      <div className="relative w-full max-w-7xl h-[90vh] bg-gray-900 rounded-xl shadow-2xl border border-gray-700 flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-700 flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-white">Customize Theme</h2>
            <p className="text-sm text-gray-400 mt-1">
              Create your perfect color scheme
            </p>
          </div>
          <button
            onClick={handleCancel}
            className="p-2 hover:bg-gray-800 rounded-lg transition-colors"
          >
            <svg className="w-6 h-6 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 flex overflow-hidden">
          {/* Left Panel - Controls */}
          <div className="w-1/2 border-r border-gray-700 flex flex-col">
            {/* Tabs */}
            <div className="px-4 py-3 border-b border-gray-700">
              <div className="flex gap-2 overflow-x-auto">
                {tabs.map(tab => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`
                      px-4 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap
                      ${activeTab === tab.id
                        ? 'bg-purple-600'
                        : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                      }
                    `}
                  >
                    <span className="mr-2">{tab.icon}</span>
                    {tab.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Color Controls */}
            <div className="flex-1 overflow-y-auto p-6 space-y-6">
              {activeTab === 'background' && (
                <>
                  <ColorPicker
                    label="Primary Background"
                    value={editingTheme.colors.background.primary}
                    onChange={(v) => updateColor('background.primary', v)}
                  />
                  <ColorPicker
                    label="Secondary Background"
                    value={editingTheme.colors.background.secondary}
                    onChange={(v) => updateColor('background.secondary', v)}
                  />
                  <ColorPicker
                    label="Tertiary Background"
                    value={editingTheme.colors.background.tertiary}
                    onChange={(v) => updateColor('background.tertiary', v)}
                  />
                  <ColorPicker
                    label="Elevated Background (Cards/Modals)"
                    value={editingTheme.colors.background.elevated}
                    onChange={(v) => updateColor('background.elevated', v)}
                  />
                  <ColorPicker
                    label="Hover State"
                    value={editingTheme.colors.background.hover}
                    onChange={(v) => updateColor('background.hover', v)}
                  />
                </>
              )}

              {activeTab === 'text' && (
                <>
                  <ColorPicker
                    label="Primary Text"
                    value={editingTheme.colors.text.primary}
                    onChange={(v) => updateColor('text.primary', v)}
                    showContrast
                    contrastBackground={editingTheme.colors.background.primary}
                  />
                  <ColorPicker
                    label="Secondary Text"
                    value={editingTheme.colors.text.secondary}
                    onChange={(v) => updateColor('text.secondary', v)}
                    showContrast
                    contrastBackground={editingTheme.colors.background.primary}
                  />
                  <ColorPicker
                    label="Muted Text"
                    value={editingTheme.colors.text.muted}
                    onChange={(v) => updateColor('text.muted', v)}
                  />
                  <ColorPicker
                    label="Disabled Text"
                    value={editingTheme.colors.text.disabled}
                    onChange={(v) => updateColor('text.disabled', v)}
                  />
                  <ColorPicker
                    label="Inverse Text (for light backgrounds)"
                    value={editingTheme.colors.text.inverse}
                    onChange={(v) => updateColor('text.inverse', v)}
                  />
                </>
              )}

              {activeTab === 'border' && (
                <>
                  <ColorPicker
                    label="Default Border"
                    value={editingTheme.colors.border.default}
                    onChange={(v) => updateColor('border.default', v)}
                  />
                  <ColorPicker
                    label="Subtle Border"
                    value={editingTheme.colors.border.subtle}
                    onChange={(v) => updateColor('border.subtle', v)}
                  />
                  <ColorPicker
                    label="Strong Border"
                    value={editingTheme.colors.border.strong}
                    onChange={(v) => updateColor('border.strong', v)}
                  />
                  <ColorPicker
                    label="Accent Border"
                    value={editingTheme.colors.border.accent}
                    onChange={(v) => updateColor('border.accent', v)}
                  />
                </>
              )}

              {activeTab === 'accent' && (
                <>
                  <ColorPicker
                    label="Primary Accent"
                    value={editingTheme.colors.accent.primary}
                    onChange={(v) => updateColor('accent.primary', v)}
                  />
                  <ColorPicker
                    label="Secondary Accent"
                    value={editingTheme.colors.accent.secondary}
                    onChange={(v) => updateColor('accent.secondary', v)}
                  />
                  <ColorPicker
                    label="Light Accent"
                    value={editingTheme.colors.accent.light}
                    onChange={(v) => updateColor('accent.light', v)}
                  />
                  <ColorPicker
                    label="Hover Accent"
                    value={editingTheme.colors.accent.hover}
                    onChange={(v) => updateColor('accent.hover', v)}
                  />
                  <ColorPicker
                    label="Disabled Accent"
                    value={editingTheme.colors.accent.disabled}
                    onChange={(v) => updateColor('accent.disabled', v)}
                  />
                </>
              )}

              {activeTab === 'semantic' && (
                <>
                  <ColorPicker
                    label="Success"
                    value={editingTheme.colors.success}
                    onChange={(v) => updateColor('success', v)}
                  />
                  <ColorPicker
                    label="Warning"
                    value={editingTheme.colors.warning}
                    onChange={(v) => updateColor('warning', v)}
                  />
                  <ColorPicker
                    label="Error"
                    value={editingTheme.colors.error}
                    onChange={(v) => updateColor('error', v)}
                  />
                  <ColorPicker
                    label="Info"
                    value={editingTheme.colors.info}
                    onChange={(v) => updateColor('info', v)}
                  />
                </>
              )}

              {activeTab === 'other' && (
                <>
                  <ColorPicker
                    label="Hover State"
                    value={editingTheme.colors.hover}
                    onChange={(v) => updateColor('hover', v)}
                  />
                  <ColorPicker
                    label="Focus State"
                    value={editingTheme.colors.focus}
                    onChange={(v) => updateColor('focus', v)}
                  />
                  <ColorPicker
                    label="Active State"
                    value={editingTheme.colors.active}
                    onChange={(v) => updateColor('active', v)}
                  />
                  <ColorPicker
                    label="Disabled State"
                    value={editingTheme.colors.disabled}
                    onChange={(v) => updateColor('disabled', v)}
                  />
                </>
              )}
            </div>

            {/* Preset Reset Buttons */}
            <div className="p-4 border-t border-gray-700">
              <p className="text-sm text-gray-400 mb-3">Quick Reset</p>
              <div className="flex gap-2">
                <button
                  onClick={() => handleReset('dark')}
                  className="flex-1 px-4 py-2 bg-gray-800 hover:bg-gray-700 text-white rounded-lg transition-colors text-sm font-medium"
                >
                  🌙 Reset to Dark
                </button>
                <button
                  onClick={() => handleReset('light')}
                  className="flex-1 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-900 rounded-lg transition-colors text-sm font-medium"
                >
                  ☀️ Reset to Light
                </button>
              </div>
            </div>
          </div>

          {/* Right Panel - Live Preview */}
          <div className="w-1/2 p-6 bg-gray-800/30">
            <h3 className="text-lg font-semibold text-white mb-4">Live Preview</h3>
            <ThemePreview theme={editingTheme} />
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-700 flex items-center justify-between">
          <div className="text-sm text-gray-400">
            {hasChanges && (
              <span className="text-yellow-400">● Unsaved changes</span>
            )}
          </div>
          <div className="flex gap-3">
            <button
              onClick={handleCancel}
              className="px-6 py-2 bg-gray-800 hover:bg-gray-700 text-white rounded-lg transition-colors font-medium"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={!hasChanges || !canEditTheme}
              className="px-6 py-2 bg-purple-600 hover:bg-purple-700 rounded-lg transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ color: 'var(--color-text-on-accent)' }}
            >
              Save Theme
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
